import { z } from 'zod';
import { SkillType } from '@types';
import { keyValueSchema, locationSchema, vatSchema } from '../zod-schemas/schema';

const tempWorkSchema = z.object({
  grossHourlyRateCents: z.number(),
  cla: z.string(),
  ortApplicable: z.boolean().optional(),
});

const freelanceSchema = z.object({
  hourlyRateCents: z.number(),
  mutableUntilMinutes: z.number(),
  ortApplicable: z.boolean().optional(),
});

const jobRequirementsSchema = z.object({
  skills: z.array(keyValueSchema),
  language: z.array(keyValueSchema),
  apparel: z.array(keyValueSchema),
});

const flexpoolsSchema = z.object({
  id: z.string(),
  name: z.string(),
  directClaimAllowed: z.boolean(),
});

export const workTemplateSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    descriptionShort: z.string(),
    descriptionLawRequirements: z.string(),
    descriptionLong: z.string().optional(),
    contactName: z.string(),
    contactPhoneNumber: z.string(),
    location: locationSchema.nullable(),
    locationUrl: z.string(),
    departmentId: z.string().nullable(),
    breakMinutes: z.number(),
    imageUrl: z.string().nullable(),
    VAT: vatSchema,
    flexPools: z.array(flexpoolsSchema),
    flexPoolOnly: z.boolean(),
    costCenter: z.string().nullable(),
    jobTypeId: z.string().nullable(),
    tempWorkJob: tempWorkSchema.optional(),
    freelanceJob: freelanceSchema.optional(),
    isEmployee: z.boolean(),
    requiredCertificates: z.array(keyValueSchema).optional(),
    jobCertificates: z.array(keyValueSchema).optional(),
    jobRequirements: jobRequirementsSchema.optional(),
    startTime: z.string().nullable(),
    endTime: z.string().nullable(),
    maxClaimants: z.number().nullable(),
  })
  .transform((x) => ({ ...x, tempWorkJob: x.tempWorkJob, freelanceJob: x.freelanceJob }));

export const workTemplatesSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    flexPoolOnly: z.boolean(),
    lenderCompanyName: z.string().nullish(),
  })
);

export const workTemplateCreateDataSchema = z.object({
  branchOfficeNames: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      companyId: z.string(),
      departments: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
        })
      ),
    })
  ),
  jobCertificates: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    })
  ),
  breakOptions: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      minutes: z.number(),
    })
  ),
  jobGroups: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      jobType: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
        })
      ),
    })
  ),
  jobRequirements: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      type: z.nativeEnum(SkillType),
    })
  ),
  flexPools: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      departmentIds: z.array(z.string()),
    })
  ),
});

export const workTemplateDefaultsSchema = z.object({
  maxClaimants: z.number().optional(),
});

export type WorkTemplateDetail = z.infer<typeof workTemplateSchema>;
export type WorkTemplatesResponse = z.infer<typeof workTemplatesSchema>;
export type WorkTemplateCreateData = z.infer<typeof workTemplateCreateDataSchema>;
export type WorkTemplateDefaults = z.infer<typeof workTemplateDefaultsSchema>;
